import {
  createStyles,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import React from "react";
import { connect } from "react-redux";
import { APIConstants } from "../../utils/api/constants";
import { APIState } from "../../utils/api/reducer";
import { Player } from "../store/types";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      overflow: "hidden",
      "& .MuiTableCell-root": {
        borderBottom: "none",
      },
      "& .MuiTable-root": {
        margin: "0 90px",
        maxWidth: 700,
        backgroundColor: theme.palette.common.white,
        borderRadius: 17,
        [theme.breakpoints.down("xs")]: {
          margin: "0 12px",
        },
      },
      "& .headerWrapper": {
        display: "flex",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
          display: "flex",
          flexDirection: "column",
        },
      },
      "& .MuiTableCell-sizeSmall": {
        [theme.breakpoints.down("sm")]: {
          padding: "6px 6px 6px 6px",
        },
      },
    },
    dataHeader: {
      fontSize: 18,
      fontWeight: 700,
      backgroundColor: theme.palette.grey[400],
      padding: 8,
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
        padding: "8px 0",
      },
    },
    playerHeader: {
      display: "flex",
      flexDirection: "column",
      fontSize: 18,
      fontWeight: 700,
      lineHeight: 1.5,
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
        display: "flex",
        flexDirection: "column",
      },
      "& span": {
        lineHeight: 1,
      },
      "& .position": {
        fontSize: 14,
        fontWeight: 300,
        [theme.breakpoints.down("sm")]: {
          fontSize: 10,
        },
      },
      "& .plus": {
        margin: "0 4px",
      },
      "& .playerName": {
        [theme.breakpoints.down("sm")]: {
          display: "flex",
          flexDirection: "column",
        },
      },
    },
    tableBody: {
      "& .dataRow": {
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        paddingBottom: 8,
        fontSize: 16,
        [theme.breakpoints.down("xs")]: {
          fontSize: 12,
        },
      },
      "& .dataHeader": {
        fontWeight: 700,
      },
    },
  })
);

interface ReduxProps {
  readonly compareResults: APIState<Player[]>;
}

type Props = ReduxProps;

const TableComponent = (props: Props) => {
  const { compareResults } = props;
  const classes = useStyles();

  const [rowHeaders, setRowHeaders] = React.useState<string[]>([]);
  const [leftSideAtt, setLeftSideAtt] = React.useState<Player>({} as Player);
  const [rightSideAtt, setRightSideAtt] = React.useState<Player>({} as Player);

  React.useEffect(() => {
    if (compareResults.status === APIConstants.FAILED) {
      const results = compareResults.data as any;
      window.location.replace(results.data.redirectUrl);
    }
    if (compareResults.status === APIConstants.SUCCEEDED) {
      const leftSide = compareResults.data[0];
      const rightSide = compareResults.data[1];
      var headers: string[] = [];
      var side1Att: { [name: string]: string } = {};
      var side2Att: { [name: string]: string } = {};
      leftSide.players.forEach((p) => {
        p.attributes.map(a => {
          if(!headers.includes(a.key)) {
            headers.push(a.key);
          }
          side1Att[a.key] = ((parseFloat(side1Att[a.key] || '0')) + parseFloat(a.value))?.toFixed(2).toString();
          return 1
        })
      })

      rightSide.players.forEach((p) => {
        p.attributes.forEach(a => {
          side2Att[a.key] = ((parseFloat(side2Att[a.key] || '0')) + parseFloat(a.value || '0.0'))?.toFixed(2).toString();
        })
      })

      setLeftSideAtt({
        ...leftSide,
        formattedAttributes: side1Att,
      });
      setRightSideAtt({
        ...rightSide,
        formattedAttributes: side2Att,
      });
      setRowHeaders(headers);
    }
  }, [compareResults]);
  

  return (
    <TableContainer className={classes.root}>
      <Table size="small" style={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell align="center" className="headerWrapper">
              {leftSideAtt.players?.map((p, i) => {
                return (
                  <div className={classes.playerHeader} key={p.name + i}>
                    <div className="playerName">
                      {i > 0 && <span className="plus">+</span>}
                      <span>{p.name}</span>
                    </div>
                    <span className="position">
                      {p.position}-{p.team}
                    </span>
                  </div>
                );
              })}
            </TableCell>
            <TableCell align="center">
              <div className={classes.dataHeader}>Stokastic Data</div>
            </TableCell>
            <TableCell align="center" className="headerWrapper">
              {rightSideAtt.players?.map((p, i) => {
                return (
                  <div className={classes.playerHeader} key={p.name + i}>
                    <div className="playerName">
                      {i > 0 && <span className="plus">+</span>}
                      <span>{p.name}</span>
                    </div>
                    <span className="position">
                      {p.position}-{p.team}
                    </span>
                  </div>
                );
              })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {compareResults.status === APIConstants.SUCCEEDED &&
            rowHeaders?.map((header, i) => (
              <TableRow key={header + i}>
                <TableCell align="center">
                  <div className="dataRow">
                    {leftSideAtt.formattedAttributes[header]}
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow dataHeader">{header}</div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow">
                    {rightSideAtt.formattedAttributes[header]}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          {compareResults.status !== APIConstants.SUCCEEDED && (
            <>
              <TableRow>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow dataHeader">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow dataHeader">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow dataHeader">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div className="dataRow">
                    <Skeleton variant="rect" height={20} width="100%" />
                  </div>
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const mapStateToProps = (state: any) => {
  const { compareResults } = state.startSit;
  return {
    compareResults,
  };
};

export default connect(mapStateToProps, {})(TableComponent);
